import { useState, useCallback, useEffect, useRef } from "react"
import { router } from "@inertiajs/react"
import { Milkdown, MilkdownProvider, useEditor } from "@milkdown/react"
import { Editor, defaultValueCtx, rootCtx, editorViewCtx, commandsCtx } from "@milkdown/kit/core"
import {
  commonmark,
  wrapInBulletListCommand,
  wrapInOrderedListCommand,
} from "@milkdown/kit/preset/commonmark"
import { gfm } from "@milkdown/kit/preset/gfm"
import { listener, listenerCtx } from "@milkdown/kit/plugin/listener"
import { history } from "@milkdown/plugin-history"
import { Plugin, PluginKey, TextSelection } from "@milkdown/kit/prose/state"
import type { EditorView as PmEditorView } from "@milkdown/kit/prose/view"
import type { Node as PmNode } from "@milkdown/kit/prose/model"
import { sinkListItem } from "@milkdown/kit/prose/schema-list"
import { $prose } from "@milkdown/utils"
import { nord } from "@milkdown/theme-nord"
import "@milkdown/theme-nord/style.css"
import {
  Copy, Check, FileText, Circle, Loader2, MoreHorizontal,
  Pencil, FolderInput, Trash2, Clock, CalendarDays, Tag, Star, StarOff,
  ChevronUp, ChevronDown, Plus, X,
} from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { FloatingToolbar } from "./FloatingToolbar"
import { LinkPreviewPopover } from "./LinkPreviewPopover"
import { FolderPicker } from "@/components/capture/FolderPicker"
import type { SaveStatus } from "@/hooks/useAutoSave"
import type { FrontMatterField, FrontMatterValues } from "@/types/frontMatter"

interface MarkdownEditorProps {
  filePath: string | null
  initialBody: string
  initialFrontMatter: FrontMatterValues
  frontMatterFields: FrontMatterField[]
  saveStatus: SaveStatus
  onContentChange: (frontMatter: FrontMatterValues, body: string) => void
  onSave?: () => void
  clientModified?: string | null
  serverModified?: string | null
  autoFocus?: boolean
  newNote?: boolean
  rootFolder?: string
}

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute("content") || ""
}

// Markdown can't represent a trailing empty paragraph, so a fresh `# Title\n`
// note parses as a single heading. Insert a paragraph after it so the cursor
// lands on a fresh line for body text rather than inside the heading.
function placeCursorAfterFirstHeading(view: PmEditorView) {
  const { state } = view
  const { doc, schema } = state
  const paragraphType = schema.nodes.paragraph
  if (!paragraphType) return

  const firstNode = doc.firstChild
  if (!firstNode || firstNode.type.name !== "heading") return

  const afterHeading = firstNode.nodeSize
  const nextNode = doc.maybeChild(1)
  const hasEmptyParagraph =
    nextNode?.type.name === "paragraph" && nextNode.content.size === 0

  const tr = hasEmptyParagraph
    ? state.tr
    : state.tr.insert(afterHeading, paragraphType.create())
  const cursorPos = afterHeading + 1
  try {
    tr.setSelection(TextSelection.create(tr.doc, cursorPos))
    view.dispatch(tr)
  } catch {
    // Position invalid - fall back to default focus position
  }
}

function formatDateTime(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
}

function SaveStatusIcon({
  status,
  onSave,
}: {
  status: SaveStatus
  onSave?: () => void
}) {
  const [showCheck, setShowCheck] = useState(false)
  const prevStatusRef = useRef(status)

  useEffect(() => {
    if (status === "saved" && prevStatusRef.current === "saving") {
      setShowCheck(true)
      const timer = setTimeout(() => setShowCheck(false), 1000)
      return () => clearTimeout(timer)
    }
    if (status !== "saved") {
      setShowCheck(false)
    }
    prevStatusRef.current = status
  }, [status])

  switch (status) {
    case "saved":
      if (!showCheck) return null
      return (
        <span className="flex items-center text-emerald-600 dark:text-emerald-400" title="Saved">
          <Check className="w-4 h-4" strokeWidth={2} />
        </span>
      )
    case "saving":
      return (
        <span className="flex items-center text-secondary" title="Saving...">
          <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
        </span>
      )
    case "unsaved":
      return (
        <button
          onClick={onSave}
          className="flex items-center text-secondary hover:text-secondary/80 transition-colors cursor-pointer"
          title="Unsaved changes"
        >
          <Circle className="w-2.5 h-2.5 fill-current" />
        </button>
      )
    case "transcribing":
      return (
        <span className="flex items-center text-secondary" title="Transcribing...">
          <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
        </span>
      )
    default:
      return null
  }
}

function MilkdownEditor({
  initialBody,
  onMarkdownChange,
  autoFocus = false,
  newNote = false,
}: {
  initialBody: string
  onMarkdownChange: (md: string) => void
  autoFocus?: boolean
  newNote?: boolean
}) {
  const editorRef = useRef<ReturnType<typeof useEditor>>(null)
  const focusedRef = useRef(false)

  const taskListClickPlugin = $prose(() => {
    return new Plugin({
      key: new PluginKey("task-list-click"),
      props: {
        handleClick(view, pos, event) {
          const target = event.target as HTMLElement
          const li = target.closest('li[data-item-type="task"]') as HTMLElement | null
          if (!li) return false

          // Only toggle when clicking the ::before checkbox area (left 1.5rem of the li)
          const liRect = li.getBoundingClientRect()
          if (event.clientX > liRect.left + 24) return false

          const $pos = view.state.doc.resolve(pos)
          let depth = $pos.depth
          while (depth > 0) {
            const node = $pos.node(depth)
            if (node.type.name === "list_item" && node.attrs.checked != null) {
              const tr = view.state.tr.setNodeMarkup(
                $pos.before(depth),
                undefined,
                { ...node.attrs, checked: !node.attrs.checked }
              )
              view.dispatch(tr)
              return true
            }
            depth--
          }
          return false
        },
      },
    })
  })

  const tableScrollPlugin = $prose(() => {
    return new Plugin({
      key: new PluginKey("table-scroll-wrapper"),
      props: {
        nodeViews: {
          table(node: PmNode, view: PmEditorView, getPos: (() => number | undefined) | boolean) {
            const wrapper = document.createElement("div")
            wrapper.className = "table-scroll-wrapper"
            const contentDOM = document.createElement("table")
            wrapper.appendChild(contentDOM)
            return { dom: wrapper, contentDOM }
          },
        },
      },
    })
  })

  // Mobile: typing 2 spaces at the start of a list item indents it (replaces Tab)
  const mobileListIndentPlugin = $prose(() => {
    return new Plugin({
      key: new PluginKey("mobile-list-indent"),
      props: {
        handleTextInput(view, from, _to, text) {
          if (text !== " ") return false

          const { state } = view
          const $pos = state.doc.resolve(from)

          // Check if cursor is inside a list_item
          let listItemDepth = -1
          for (let d = $pos.depth; d > 0; d--) {
            if ($pos.node(d).type.name === "list_item") {
              listItemDepth = d
              break
            }
          }
          if (listItemDepth === -1) return false

          // Only trigger when cursor is at offset 1 in the paragraph (one space already typed)
          if ($pos.parentOffset !== 1) return false
          if ($pos.parent.textContent.charAt(0) !== " ") return false

          // Delete the leading space, prevent the second space, and sink the list item
          const listItemType = state.schema.nodes.list_item
          if (!listItemType) return false

          const tr = state.tr.delete(from - 1, from)
          view.dispatch(tr)

          // Sink the list item using ProseMirror's built-in command
          sinkListItem(listItemType)(view.state, view.dispatch)
          return true
        },
      },
    })
  })

  // Mobile: typing 2 spaces after a word inserts ". " (auto-period, like iOS/Android keyboards)
  const doubleSpacePeriodPlugin = $prose(() => {
    return new Plugin({
      key: new PluginKey("double-space-period"),
      props: {
        handleTextInput(view, from, _to, text) {
          if (text !== " ") return false
          if (from < 2) return false

          const { state } = view

          // Check if we're inside a list_item at offset 1 — skip to avoid conflict with indent
          const $pos = state.doc.resolve(from)
          if ($pos.parentOffset === 1) {
            for (let d = $pos.depth; d > 0; d--) {
              if ($pos.node(d).type.name === "list_item") return false
            }
          }

          const textBefore = state.doc.textBetween(from - 2, from, undefined, "\ufffc")
          if (textBefore.length < 2) return false

          // Pattern: word character followed by a space
          if (/\w/.test(textBefore[0]) && textBefore[1] === " ") {
            // Replace the trailing space with ". "
            const tr = state.tr.insertText(". ", from - 1, from)
            view.dispatch(tr)
            return true
          }

          return false
        },
      },
    })
  })

  // Replace `->` with `→` and `<-` with `←` as the user types. Skipped inside
  // code blocks and inline code so programming arrows (=>, ->, <-) survive.
  const arrowReplacePlugin = $prose(() => {
    return new Plugin({
      key: new PluginKey("arrow-replace"),
      props: {
        handleTextInput(view, from, _to, text) {
          if (text !== ">" && text !== "-") return false
          if (from < 1) return false

          const { state } = view
          const $pos = state.doc.resolve(from)
          for (let d = $pos.depth; d >= 0; d--) {
            if ($pos.node(d).type.name === "code_block") return false
          }
          const codeMark = state.schema.marks.code
          if (codeMark && codeMark.isInSet($pos.marks())) return false

          const charBefore = state.doc.textBetween(from - 1, from, undefined, "\ufffc")
          let replacement: string | null = null
          if (text === ">" && charBefore === "-") replacement = "→"
          else if (text === "-" && charBefore === "<") replacement = "←"
          if (!replacement) return false

          const tr = state.tr.replaceWith(from - 1, from, state.schema.text(replacement))
          view.dispatch(tr)
          return true
        },
      },
    })
  })

  // Keyboard shortcuts: Cmd/Ctrl+Shift+7 (checkbox), +8 (bullet), +9 (numbered), Cmd/Ctrl+Up/Down (move block)
  const keyboardShortcutsPlugin = $prose((ctx) => {
    const moveBlock = (view: PmEditorView, direction: -1 | 1): boolean => {
      const { state } = view
      const { $from, from, to } = state.selection

      // Find the moveable block - list_item if inside one, otherwise top-level block
      let targetDepth = 1
      for (let d = $from.depth; d > 0; d--) {
        if ($from.node(d).type.name === "list_item") {
          targetDepth = d
          break
        }
      }
      if (targetDepth < 1 || $from.depth < targetDepth) return false

      const blockStart = $from.before(targetDepth)
      const blockEnd = $from.after(targetDepth)
      const $blockStart = state.doc.resolve(blockStart)
      const parent = $blockStart.parent
      const index = $blockStart.index()

      if (direction === -1 && index === 0) return false
      if (direction === 1 && index >= parent.childCount - 1) return false

      const currentBlock = parent.child(index)
      const siblingBlock = parent.child(index + direction)

      let rangeStart: number, rangeEnd: number
      if (direction === -1) {
        rangeStart = blockStart - siblingBlock.nodeSize
        rangeEnd = blockEnd
      } else {
        rangeStart = blockStart
        rangeEnd = blockEnd + siblingBlock.nodeSize
      }

      const tr = state.tr
      if (direction === -1) {
        tr.replaceWith(rangeStart, rangeEnd, [currentBlock, siblingBlock])
      } else {
        tr.replaceWith(rangeStart, rangeEnd, [siblingBlock, currentBlock])
      }

      // Preserve selection relative to the moved block
      const newBlockStart = direction === -1 ? rangeStart : rangeStart + siblingBlock.nodeSize
      const offsetFrom = from - blockStart
      const offsetTo = to - blockStart
      const newFrom = Math.min(newBlockStart + offsetFrom, tr.doc.content.size - 1)
      const newTo = Math.min(newBlockStart + offsetTo, tr.doc.content.size - 1)
      try {
        tr.setSelection(TextSelection.create(tr.doc, newFrom, newTo))
      } catch {
        // fallback - just dispatch without adjusting selection
      }

      view.dispatch(tr.scrollIntoView())
      return true
    }

    const getListContext = (view: PmEditorView) => {
      const { state } = view
      const { $from } = state.selection
      let listItemDepth = -1
      let listDepth = -1
      for (let d = $from.depth; d > 0; d--) {
        const node = $from.node(d)
        if (node.type.name === "list_item" && listItemDepth === -1) listItemDepth = d
        if ((node.type.name === "bullet_list" || node.type.name === "ordered_list") && listDepth === -1) listDepth = d
      }
      const listItem = listItemDepth > 0 ? $from.node(listItemDepth) : null
      const listNode = listDepth > 0 ? $from.node(listDepth) : null
      const isCheckbox = listItem?.attrs.checked != null
      const isOrdered = listNode?.type.name === "ordered_list"
      const isBullet = listNode?.type.name === "bullet_list" && !isCheckbox
      return { state, $from, listItemDepth, listDepth, listItem, listNode, isCheckbox, isOrdered, isBullet }
    }

    const toggleBulletList = (view: PmEditorView): boolean => {
      const commands = ctx.get(commandsCtx)
      const { state, $from, listItemDepth, listDepth, listItem, isCheckbox, isOrdered, isBullet } = getListContext(view)

      if (listItemDepth === -1) {
        commands.call(wrapInBulletListCommand.key)
        return true
      }
      if (isBullet) {
        commands.call(wrapInBulletListCommand.key)
        return true
      }

      let tr = state.tr
      if (isOrdered) {
        tr = tr.setNodeMarkup($from.before(listDepth), state.schema.nodes.bullet_list)
      }
      if (isCheckbox && listItem) {
        const pos = tr.mapping.map($from.before(listItemDepth))
        tr = tr.setNodeMarkup(pos, undefined, { ...listItem.attrs, checked: null })
      }
      view.dispatch(tr)
      return true
    }

    const toggleOrderedList = (view: PmEditorView): boolean => {
      const commands = ctx.get(commandsCtx)
      const { state, $from, listItemDepth, listDepth, listItem, isCheckbox, isOrdered } = getListContext(view)

      if (listItemDepth === -1) {
        commands.call(wrapInOrderedListCommand.key)
        return true
      }
      if (isOrdered) {
        commands.call(wrapInOrderedListCommand.key)
        return true
      }

      let tr = state.tr
      tr = tr.setNodeMarkup($from.before(listDepth), state.schema.nodes.ordered_list)
      if (isCheckbox && listItem) {
        const pos = tr.mapping.map($from.before(listItemDepth))
        tr = tr.setNodeMarkup(pos, undefined, { ...listItem.attrs, checked: null })
      }
      view.dispatch(tr)
      return true
    }

    const toggleCheckboxList = (view: PmEditorView): boolean => {
      const commands = ctx.get(commandsCtx)
      const { state, $from, listItemDepth, listDepth, listItem, isCheckbox, isOrdered } = getListContext(view)

      if (listItemDepth === -1) {
        commands.call(wrapInBulletListCommand.key)
        const newState = view.state
        const $newFrom = newState.selection.$from
        for (let d = $newFrom.depth; d > 0; d--) {
          if ($newFrom.node(d).type.name === "list_item") {
            const tr = newState.tr.setNodeMarkup($newFrom.before(d), undefined, {
              ...$newFrom.node(d).attrs,
              checked: false,
            })
            view.dispatch(tr)
            break
          }
        }
        return true
      }
      if (isCheckbox) {
        commands.call(wrapInBulletListCommand.key)
        return true
      }

      let tr = state.tr
      if (isOrdered) {
        tr = tr.setNodeMarkup($from.before(listDepth), state.schema.nodes.bullet_list)
      }
      if (listItem) {
        const pos = tr.mapping.map($from.before(listItemDepth))
        tr = tr.setNodeMarkup(pos, undefined, { ...listItem.attrs, checked: false })
      }
      view.dispatch(tr)
      return true
    }

    return new Plugin({
      key: new PluginKey("keyboard-shortcuts"),
      props: {
        handleKeyDown(view, event) {
          const mod = event.metaKey || event.ctrlKey
          if (!mod) return false

          if (event.shiftKey) {
            // Cmd/Ctrl+Shift+7: Checkbox list
            if (event.code === "Digit7") {
              event.preventDefault()
              return toggleCheckboxList(view)
            }

            // Cmd/Ctrl+Shift+8: Bullet list
            if (event.code === "Digit8") {
              event.preventDefault()
              return toggleBulletList(view)
            }

            // Cmd/Ctrl+Shift+9: Ordered list
            if (event.code === "Digit9") {
              event.preventDefault()
              return toggleOrderedList(view)
            }
          }

          // Cmd/Ctrl+Up: Move block up
          if (!event.shiftKey && event.key === "ArrowUp") {
            event.preventDefault()
            return moveBlock(view, -1)
          }

          // Cmd/Ctrl+Down: Move block down
          if (!event.shiftKey && event.key === "ArrowDown") {
            event.preventDefault()
            return moveBlock(view, 1)
          }

          return false
        },
      },
    })
  })

  const editor = useEditor((container) => {
    return Editor.make()
      .config((ctx) => {
        ctx.set(rootCtx, container)
        ctx.set(defaultValueCtx, initialBody)
        nord(ctx)

        const l = ctx.get(listenerCtx)
        l.markdownUpdated((_ctx, markdown) => {
          onMarkdownChange(markdown)
        })
      })
      .use(commonmark)
      .use(gfm)
      .use(taskListClickPlugin)
      .use(tableScrollPlugin)
      .use(mobileListIndentPlugin)
      .use(doubleSpacePeriodPlugin)
      .use(arrowReplacePlugin)
      .use(keyboardShortcutsPlugin)
      .use(history)
      .use(listener)
  }, [])

  editorRef.current = editor

  useEffect(() => {
    if (!autoFocus || focusedRef.current) return
    if (!editor.get() || editor.loading) return

    // Retry focus a few times to handle ProseMirror initialization timing
    let attempts = 0
    const maxAttempts = 5
    const tryFocus = () => {
      attempts++
      try {
        const ctx = editor.get()?.ctx
        if (ctx) {
          const view = ctx.get(editorViewCtx)
          view.focus()
          if (newNote) placeCursorAfterFirstHeading(view)
          focusedRef.current = true
          return
        }
      } catch {
        // Editor not ready yet
      }
      if (attempts < maxAttempts) {
        timer = setTimeout(tryFocus, 100)
      }
    }
    let timer = setTimeout(tryFocus, 50)

    return () => clearTimeout(timer)
  }, [autoFocus, newNote, editor.loading])

  return <Milkdown />
}

export function MarkdownEditor({
  filePath,
  initialBody,
  initialFrontMatter,
  frontMatterFields,
  saveStatus,
  onContentChange,
  onSave,
  clientModified,
  serverModified,
  autoFocus = false,
  newNote = false,
  rootFolder,
}: MarkdownEditorProps) {
  const [frontMatter, setFrontMatter] = useState<FrontMatterValues>(initialFrontMatter)
  const [showCopiedToast, setShowCopiedToast] = useState(false)
  const [editingName, setEditingName] = useState(false)
  const [editName, setEditName] = useState("")
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showFolderPicker, setShowFolderPicker] = useState(false)
  const [frontMatterExpanded, setFrontMatterExpanded] = useState(false)
  const [isFavorite, setIsFavorite] = useState(false)
  const [confirmingFmDelete, setConfirmingFmDelete] = useState<string | null>(null)
  const [addingFmField, setAddingFmField] = useState(false)
  const [newFmFieldName, setNewFmFieldName] = useState("")
  const bodyRef = useRef(initialBody)
  const renameInputRef = useRef<HTMLInputElement>(null)
  const newFmFieldInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!filePath) return
    fetch("/api/favorites/check", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
      body: JSON.stringify({ paths: [filePath] }),
    })
      .then((res) => res.json())
      .then((data) => setIsFavorite((data.favorited || []).includes(filePath)))
      .catch(() => {})
  }, [filePath])

  async function handleToggleFavorite() {
    if (!filePath) return
    const name = filePath.split("/").pop() || ""
    try {
      if (isFavorite) {
        await fetch("/api/favorites", {
          method: "DELETE",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
          body: JSON.stringify({ path: filePath }),
        })
        setIsFavorite(false)
      } else {
        await fetch("/api/favorites", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
          body: JSON.stringify({ path: filePath, name, item_type: "file" }),
        })
        setIsFavorite(true)
      }
    } catch {
      // ignore
    }
  }

  const handleMarkdownChange = useCallback(
    (markdown: string) => {
      bodyRef.current = markdown
      onContentChange(frontMatter, markdown)
    },
    [frontMatter, onContentChange]
  )

  function handleFrontMatterChange(newFm: FrontMatterValues) {
    setFrontMatter(newFm)
    onContentChange(newFm, bodyRef.current)
  }

  useEffect(() => {
    if (addingFmField && newFmFieldInputRef.current) {
      newFmFieldInputRef.current.focus()
    }
  }, [addingFmField])

  function handleDeleteFmField(key: string) {
    const newValues = { ...frontMatter }
    delete newValues[key]
    handleFrontMatterChange(newValues)
    setConfirmingFmDelete(null)
  }

  function handleAddFmField() {
    const name = newFmFieldName.trim()
    if (!name) return
    const key = name.toLowerCase().replace(/\s+/g, "_")
    if (frontMatter[key] !== undefined || registeredNames.has(key)) return
    handleFrontMatterChange({ ...frontMatter, [key]: "" })
    setNewFmFieldName("")
    setAddingFmField(false)
  }

  function isFmFieldEmpty(value: unknown): boolean {
    if (value === null || value === undefined) return true
    if (value === "") return true
    if (Array.isArray(value) && value.length === 0) return true
    return false
  }

  async function handleCopy() {
    const fmEntries = Object.entries(frontMatter)
    let fmStr = ""
    if (fmEntries.length > 0) {
      const lines = fmEntries.map(([k, v]) => {
        if (Array.isArray(v)) {
          return `${k}:\n${v.map((item) => `- ${item}`).join("\n")}`
        }
        if (v === null || v === undefined) return `${k}:`
        return `${k}: ${v}`
      })
      fmStr = `---\n${lines.join("\n")}\n---\n`
    }
    await navigator.clipboard.writeText(fmStr + bodyRef.current)
    setShowCopiedToast(true)
    setTimeout(() => setShowCopiedToast(false), 2000)
  }

  const pathParts = filePath?.split("/").filter(Boolean) || []
  const fileName = pathParts.pop() || "Untitled"
  const folderPath = pathParts

  const registeredNames = new Set(frontMatterFields.map((f) => f.name))
  const unregisteredEntries = Object.entries(frontMatter).filter(([key]) => !registeredNames.has(key))


  function startRename() {
    setEditName(fileName)
    setEditingName(true)
    setTimeout(() => renameInputRef.current?.select(), 0)
  }

  async function commitRename() {
    setEditingName(false)
    const trimmed = editName.trim()
    if (!trimmed || trimmed === fileName || !filePath) return

    const parts = filePath.split("/")
    parts[parts.length - 1] = trimmed
    const newPath = parts.join("/")

    await fetch("/api/files/move", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCSRFToken(),
      },
      body: JSON.stringify({ from_path: filePath, to_path: newPath }),
    })

    const encodedPath = newPath.replace(/^\/+/, "").split("/").map(s => encodeURIComponent(s)).join("/")
    router.visit(`/files/${encodedPath}`)
  }

  async function handleDelete() {
    if (!filePath) return
    const encodedPath = filePath.replace(/^\/+/, "").split("/").map(s => encodeURIComponent(s)).join("/")
    await fetch(`/api/files/${encodedPath}`, {
      method: "DELETE",
      headers: { "X-CSRF-Token": getCSRFToken() },
    })
    setShowDeleteConfirm(false)
    router.visit("/files")
  }

  async function handleMove(destination: string) {
    if (!filePath) return
    const name = filePath.split("/").pop()
    const newPath = `${destination}/${name}`

    await fetch("/api/files/move", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCSRFToken(),
      },
      body: JSON.stringify({ from_path: filePath, to_path: newPath }),
    })
    setShowFolderPicker(false)

    const encodedPath = newPath.replace(/^\/+/, "").split("/").map(s => encodeURIComponent(s)).join("/")
    router.visit(`/files/${encodedPath}`)
  }

  const fullPath = filePath || ""
  const displayPath = fullPath.replace(/^\/+/, "")

  return (
    <div className="min-h-[calc(100vh-3.5rem)]" data-testid="markdown-editor">
      {/* Editor body */}
      <div className="max-w-4xl mx-auto px-3 sm:px-8 py-6 pb-32">
        {/* Info bar: light gray bg, bottom border, collapsed/expanded */}
        <div className="bg-muted/50 dark:bg-muted -mx-3 sm:mx-0 sm:rounded-t-lg sm:border sm:shadow-xs overflow-hidden border-b border-border">
          {/* Top bar — always visible */}
          <div className="flex w-full items-center gap-2 px-4 py-2.5">
            <div className="flex items-center gap-2 min-w-0 flex-1 group/name">
              <FileText className="w-3.5 h-3.5 text-muted-foreground/60 shrink-0" strokeWidth={1.5} />
              {editingName ? (
                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                  <input
                    ref={renameInputRef}
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitRename()
                      if (e.key === "Escape") setEditingName(false)
                    }}
                    onBlur={commitRename}
                    className="text-xs font-semibold text-foreground bg-card border border-input rounded px-2 py-1 flex-1 min-w-0 outline-none focus:border-ring focus:ring-ring/50 focus:ring-[3px] transition-[color,box-shadow]"
                    autoFocus
                  />
                  <button
                    onClick={() => setShowFolderPicker(true)}
                    className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors shrink-0"
                    title="Move to folder"
                  >
                    <FolderInput className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={startRename}
                  className="flex items-center gap-1.5 min-w-0 text-left"
                  title="Click to rename"
                >
                  <span className="text-xs font-semibold text-foreground truncate">
                    {displayPath || "Untitled"}
                  </span>
                  <Pencil className="w-3 h-3 text-muted-foreground/0 group-hover/name:text-muted-foreground transition-colors shrink-0" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <SaveStatusIcon status={saveStatus} onSave={onSave} />
              <button
                onClick={handleToggleFavorite}
                className="p-1 rounded-md hover:bg-accent transition-colors"
                title={isFavorite ? "Remove from Favorites" : "Add to Favorites"}
              >
                {isFavorite ? (
                  <Star className="w-4 h-4 fill-secondary text-secondary" />
                ) : (
                  <Star className="w-4 h-4 text-muted-foreground" />
                )}
              </button>
              <button
                onClick={handleCopy}
                className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-all"
                title="Copy to clipboard"
                data-testid="copy-button"
              >
                <Copy className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-all"
                    data-testid="editor-settings-menu"
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={startRename} className="gap-2">
                    <Pencil className="h-4 w-4" />
                    Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowFolderPicker(true)} className="gap-2">
                    <FolderInput className="h-4 w-4" />
                    Move
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setShowDeleteConfirm(true)}
                    className="gap-2 text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <button
                onClick={() => setFrontMatterExpanded(!frontMatterExpanded)}
                className="p-1 rounded-md text-muted-foreground/50 hover:text-muted-foreground hover:bg-accent transition-colors"
              >
                {frontMatterExpanded ? (
                  <ChevronDown className="w-3.5 h-3.5" />
                ) : (
                  <ChevronUp className="w-3.5 h-3.5" />
                )}
              </button>
            </div>
          </div>

          {/* Expanded panel */}
          {frontMatterExpanded && (
            <div className="border-t border-border px-4 py-4 space-y-4" data-testid="info-bar-expanded">
              {/* Dates */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                {clientModified && (
                  <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <CalendarDays className="w-3 h-3" />
                    Created {formatDateTime(clientModified)}
                  </span>
                )}
                {serverModified && (
                  <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    Updated {formatDateTime(serverModified)}
                  </span>
                )}
              </div>

              {/* Front matter fields */}
              <div className="border-t border-border pt-3.5 space-y-3">
                {frontMatterFields.map((field) => (
                  <div key={field.id}>
                    <p className="text-[11px] font-medium text-muted-foreground mb-1.5 capitalize tracking-wide">
                      {field.name}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {field.options.map((option) => {
                        const isSelected = frontMatter[field.name] === option.label
                        return (
                          <button
                            key={option.id}
                            onClick={() => {
                              const newValues = { ...frontMatter }
                              if (newValues[field.name] === option.label) {
                                delete newValues[field.name]
                              } else {
                                newValues[field.name] = option.label
                              }
                              handleFrontMatterChange(newValues)
                            }}
                            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-150 ${
                              isSelected
                                ? "bg-secondary text-secondary-foreground shadow-xs shadow-secondary/25 scale-[1.02]"
                                : "bg-card border text-muted-foreground hover:border-border hover:text-foreground hover:shadow-xs active:scale-[0.98]"
                            }`}
                            data-testid={`fm-${field.name}-${option.label}`}
                          >
                            {option.label}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                ))}

                {unregisteredEntries.length > 0 && frontMatterFields.length > 0 && (
                  <div className="border-t border-border pt-3" />
                )}

                {unregisteredEntries.map(([key, value]) => (
                  <div key={key}>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <p className="text-[11px] font-medium text-muted-foreground capitalize tracking-wide">
                        {key.replace(/_/g, " ")}
                      </p>
                      {isFmFieldEmpty(value) && (
                        <>
                          {confirmingFmDelete === key ? (
                            <div className="flex items-center gap-1.5 ml-auto">
                              <span className="text-[10px] text-muted-foreground">Remove?</span>
                              <button
                                onClick={() => handleDeleteFmField(key)}
                                className="text-[10px] font-medium text-destructive hover:text-destructive/80 transition-colors"
                              >
                                Yes
                              </button>
                              <button
                                onClick={() => setConfirmingFmDelete(null)}
                                className="text-[10px] font-medium text-muted-foreground hover:text-foreground transition-colors"
                              >
                                No
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setConfirmingFmDelete(key)}
                              className="p-0.5 text-muted-foreground/40 hover:text-destructive transition-colors rounded"
                              title="Remove field"
                            >
                              <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                    <input
                      type="text"
                      value={(() => {
                        if (value === null || value === undefined) return ""
                        if (Array.isArray(value)) return value.join(", ")
                        return String(value)
                      })()}
                      onChange={(e) => {
                        const newValues = { ...frontMatter }
                        if (Array.isArray(value)) {
                          newValues[key] = e.target.value.split(",").map((s) => s.trim()).filter(Boolean)
                        } else {
                          const trimmed = e.target.value.trim()
                          if (trimmed === "") newValues[key] = ""
                          else if (trimmed === "true") newValues[key] = true
                          else if (trimmed === "false") newValues[key] = false
                          else if (/^-?\d+(\.\d+)?$/.test(trimmed)) newValues[key] = Number(trimmed)
                          else newValues[key] = e.target.value
                        }
                        handleFrontMatterChange(newValues)
                      }}
                      placeholder={Array.isArray(value) ? "comma-separated values" : ""}
                      className="w-full px-3 py-1.5 rounded-md text-xs bg-card border text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-ring transition-colors"
                    />
                  </div>
                ))}

                {/* Add new field */}
                {addingFmField ? (
                  <div className="flex items-center gap-1.5">
                    <input
                      ref={newFmFieldInputRef}
                      type="text"
                      value={newFmFieldName}
                      onChange={(e) => setNewFmFieldName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleAddFmField()
                        if (e.key === "Escape") { setAddingFmField(false); setNewFmFieldName("") }
                      }}
                      placeholder="Field name..."
                      className="flex-1 px-3 py-1.5 rounded-md text-xs bg-card border text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-ring transition-colors"
                    />
                    <button
                      onClick={handleAddFmField}
                      disabled={!newFmFieldName.trim()}
                      className="px-2.5 py-1.5 rounded-md text-xs font-medium bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors disabled:opacity-40"
                    >
                      Add
                    </button>
                    <button
                      onClick={() => { setAddingFmField(false); setNewFmFieldName("") }}
                      className="p-1 text-muted-foreground/50 hover:text-muted-foreground transition-colors rounded-md"
                    >
                      <X className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setAddingFmField(true)}
                    className="flex items-center gap-1 text-[11px] text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                  >
                    <Plus className="w-3 h-3" strokeWidth={2} />
                    <span>Add field</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Content card */}
        <div className="bg-card -mx-3 p-8 sm:mx-0 sm:rounded-b-lg sm:border sm:border-t-0 sm:shadow-xs">
          <div
            className="prose-editor relative focus-within:outline-none min-h-[60vh] cursor-text milkdown-wrapper"
            onClick={(e) => {
              const pm = (e.currentTarget as HTMLElement).querySelector(".ProseMirror") as HTMLElement
              if (pm && !pm.contains(e.target as Node)) {
                pm.focus()
              }
            }}
          >
            <MilkdownProvider>
              <MilkdownEditor initialBody={initialBody} onMarkdownChange={handleMarkdownChange} autoFocus={autoFocus} newNote={newNote} />
              <FloatingToolbar />
              <LinkPreviewPopover />
            </MilkdownProvider>
          </div>
        </div>
      </div>

      {/* Copied toast */}
      <div
        className={`fixed bottom-36 right-4 md:bottom-[4.5rem] md:right-8 z-40 transition-all duration-200 ${
          showCopiedToast
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-2 pointer-events-none"
        }`}
      >
        <div className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-foreground text-background text-xs font-medium shadow-lg">
          <Check className="w-3 h-3" strokeWidth={2.5} />
          Copied!
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete file</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{fileName}&quot;? This will remove it from Dropbox.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move folder picker */}
      <FolderPicker
        open={showFolderPicker}
        onClose={() => setShowFolderPicker(false)}
        onSelect={handleMove}
        rootPath={rootFolder}
      />
    </div>
  )
}
